import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../lib/types';
import { apiMe, apiLogout, setNavigate, setAuthUser, setAccessToken } from '../lib/api';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  setUser: (u: User | null) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  setUser: () => {},
  logout: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Register navigate and setUser with the axios interceptor IMMEDIATELY
  // (before apiMe() is called) so it can redirect to /login and clear state
  // on token refresh failure. This must happen before apiMe() to avoid race condition.
  setNavigate((path) => navigate(path));
  setAuthUser((u) => setUser(u));

  useEffect(() => {
    // Restore access token from localStorage if present
    const token = localStorage.getItem('access_token');
    if (token) {
      setAccessToken(token);
    }

    apiMe()
      .then((u) => setUser(u))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, [navigate]);

  const logout = async () => {
    await apiLogout().catch(() => {});
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, setUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
